<script lang="ts" setup>
import { InputDateTimeCalendar, InputMask, CollapseContent, ButtonComp, ValidatorMessage, NullSetter } from '@/components';
import PopupRelativeTo from '@/components/layout/PopupRelativeTo.vue';
import { useClickOutside, useLanguageChanged, useNullsetterListener, useValidator } from '@/composeables';

import InputMaskData from '@/objects/InputMaskData';
import type { IEditorComponent, ValidatorDef } from '@/objects/gui';
import { useEventCommonStoreRef } from '@/stores';
import type { ElementOrComponent } from '@/utilities/VueHelpers';
import {
    formatDateToInputFormat,
    type DateType,
    parseDateStringIntoDateFromCurrentFormat,
    dateFormatHasTime,
    dateFormatHasDate,
    getDefaultDateWithDate,
} from '@/utilities/dateutils';
import { computed, ref, watch } from 'vue';

// REMEMBER TO ADD THE SAME TO THE NON-NULLABLE InputDateTime IF YOU CHANGE ANYTHING HERE
const props = withDefaults(
    defineProps<{
        modelValue: Date | null;

        /**
         * The format to use.
         */
        format: DateType;

        /**
         * The validator for the input
         * @default null
         */
        validator?: ValidatorDef | null;

        /**
         * If the date/time selector should be hidden
         * @default false
         */
        hideSelector?: boolean;

        /**
         * If the nullsetter should be shown
         * @default false
         * */
        showNullSetter?: boolean;

        /**
         * Autofills missing values in the timepart when the user blurs the input
         *
         * This allows for fast partial input of the time
         *
         * This only fills the timepart, not the datepart since the datepart doesnt have a natural way to autofill
         *
         * Atleast the hour part of the time has to be filled, anything else will be set to 0
         *
         * Practically this means that you can put 12:3 and it will be autofilled to 12:30:00.000
         */
        autoFillOnBlur?: boolean;

        /**
         * Date that will prefill the datePart of the box
         */
        prefillDate?: Date;

        /**
         * If the prefill date should be taken from the event date, this will override the prefillDate prop
         */
        prefillUsingEventDate?: boolean;

        /**
         * Default date to use when only time is shown, when not filled we use defaultDateTimeForIncompleteFormats from dateutils
         */
        defaultDateForTimeOnlyInput?: Date;

        disabled?: boolean;
    }>(),
    {
        validator: null,
        hideSelector: false,
        showNullSetter: false,
        prefillMs: false,
        autoFillOnBlur: false,
        prefillUsingEventDate: false,
    },
);

const emits = defineEmits<{
    (e: 'update:modelValue', val: Date | null): void;
    (e: 'change', newval: Date, oldval: Date | null): void;
}>();

const previousValue = ref<Date | null>(props.modelValue);

const typesThatTriggerCalendar: DateType[] = ['DATE', 'DATETIME', 'DATETIMEMS1', 'DATETIMEMS2', 'DATETIMEMS3'];

const cssClassForDateType = new Map<DateType, string>();
cssClassForDateType.set('DATETIME', 'calendar-datetime');
cssClassForDateType.set('DATETIMEMS3', 'calendar-datetimems3');
cssClassForDateType.set('TIMEMS3', 'calendar-timems3');
cssClassForDateType.set('DATE', 'calendar-date');
// Default to calendar-datetime if we dont have a class for the format since it was hardcoded for that before
// TODO: Add the rest if we want to do it this way

const inputDateTimeInstance = ref<InstanceType<typeof InputDateTimeCalendar> | null>(null);
const inputMaskInstance = ref<InstanceType<typeof InputMask> | null>(null);
const calendarButton = ref<InstanceType<typeof ButtonComp> | null>(null);

const calendarOpen = ref(false);

// The current date selected in the calendar
const internalDate = ref(getDefaultDateWithDate(props.modelValue ? new Date(props.modelValue) : new Date()));
// The value that should be shown in the mask input
const internalMaskValue = ref(props.modelValue ? formatDateToInputFormat(props.modelValue, props.format) : '');

const calendarDateSelected = (date: Date) => {
    // Parse the picked date and set that in the current mask value
    const parsedDate = formatDateToInputFormat(date, 'DATE');
    maskValue.value = parsedDate + imd.getCurrentDisplayText().substring(parsedDate.length);
    toggleCalender(false);
};

// Computed to sync the inputMask and the datePicker
const maskValue = computed({
    get() {
        return internalMaskValue.value;
    },
    set(value) {
        internalMaskValue.value = value;
        internalDate.value = getDefaultDateWithDate(
            parseDateStringIntoDateFromCurrentFormat(value, props.format, internalDate.value, props.defaultDateForTimeOnlyInput),
        );
    },
});

// Create an inputMask for the current date format
let imd = InputMaskData.CreateFromDateFormat(props.format);

if (props.prefillDate) {
    if (dateFormatHasDate(props.format)) {
        maskValue.value = formatDateToInputFormat(props.prefillDate, 'DATE');
    }
}

// Set inital maskValue if we have a date
if (props.modelValue) {
    maskValue.value = formatDateToInputFormat(props.modelValue, props.format);
}

/**
 * If we have the setting to prefill using the event date, we watch the event date and update the maskValue accordingly when it changes
 */
if (props.prefillUsingEventDate && props.modelValue === null && dateFormatHasDate(props.format)) {
    const { Event } = useEventCommonStoreRef();

    maskValue.value = Event.value?.Date ? formatDateToInputFormat(Event.value?.Date, 'DATE') : '';
}

// The validate function
const { Validate, ValidateMessage } = useValidator(props.validator, props.modelValue)!;

// Watch the modelvalue so we listen to changes from outside
watch(
    () => props.modelValue,
    (newVal, oldval) => {
        if (newVal) {
            maskValue.value = formatDateToInputFormat(newVal, props.format);
        } else {
            // Backspacing in the inputdatetime will make the date invalid and thus emit NULL.
            // The bound value will update on the outside and we will get a new modelValue here.
            // That has the annoying sideeffect of emptying the inputbox when we start changing the value.
            // To avoid this we dont empty the text value if the focus is inside the box.
            if (!inputMaskInstance.value?.isFocused()) {
                maskValue.value = '';
            }
        }
    },
);

// If nullsetter is clicked and we arent focused, clear the input
useNullsetterListener(() => {
    maskValue.value = '';
});

const reloadFormat = (oldFormat: DateType, newFormat: DateType) => {
    // When the format changes we recreate the mask, which will use the new format
    const newimd = InputMaskData.CreateFromDateFormat(props.format);

    // unfilledMask is unfilled since we are getting it from a newly created InputMask, so this has no user data yet
    let unfilledMask = newimd.getCurrentDisplayText();

    // Replace each of the dateparts.
    // For the ms we have to do all 3 variants since we dont know which one it is
    ['yyyy', 'MM', 'dd', 'hh', 'mm', 'ss', 'fff', 'ff', 'f'].forEach((dp) => {
        const currentImdValue = imd.getCurrentStringForMask(dp);

        // If we cant find a value, then that part isent part of the old format.
        // This can happen if we have 2 decimals and we look for 'fff' for example.
        if (!currentImdValue) {
            return;
        }

        // Replace the current value into the new mask
        unfilledMask = unfilledMask.replace(dp, currentImdValue);
    });

    // If we have a prefillDate and we changed from a format without date to a format with date, we should prefill the date
    // Since the date has been removed from the emitted value when the format was time only
    if (props.prefillDate && !dateFormatHasDate(oldFormat) && dateFormatHasDate(newFormat)) {
        unfilledMask = unfilledMask.replace('yyyy', props.prefillDate.getFullYear().toString());
        unfilledMask = unfilledMask.replace('MM', (props.prefillDate.getMonth() + 1).toString().padStart(2, '0'));
        unfilledMask = unfilledMask.replace('dd', props.prefillDate.getDate().toString().padStart(2, '0'));
    }

    // Overwrite the imd since we have a new one with a new format now
    imd = newimd;

    // Set the correct string for the imd
    imd.setStringValue(unfilledMask);

    maskValue.value = imd.getCurrentDisplayText();
};

// Detect language change so we can change the dateformat in the inputmask
useLanguageChanged(() => reloadFormat(props.format, props.format));

// Changes to the format should reload the format
watch(
    () => props.format,
    (nv, ov) => reloadFormat(ov, nv),
);

const toggleCalender = (val: boolean) => (calendarOpen.value = val);

// Close the calendar if we click outside of the datetimeinput
useClickOutside([inputDateTimeInstance, inputMaskInstance, calendarButton] as Array<ElementOrComponent>, () => toggleCalender(false));

const emitStringAsDate = (val: string | null) => {
    if (val === null) {
        emits('update:modelValue', null);
        return;
    }

    // Validate the input
    if (props.validator) {
        Validate(val);
    }

    const date = parseDateStringIntoDateFromCurrentFormat(val, props.format, internalDate.value, props.defaultDateForTimeOnlyInput);
    emits('update:modelValue', date);

    emits('change', date, previousValue.value);
    previousValue.value = date;
};

const maskValueChanged = (newVal: string) => {
    // Here we should emit NULL if the value isent valid
    if (!imd.isMaskFilled()) {
        emitStringAsDate(null);
    }
    maskValue.value = newVal;
};

// If the input has had focus. We use this to show the validation message
const hasHadFocus = ref(false);

const onBlur = () => {
    hasHadFocus.value = true;
    autoFillOnBlur();
};

/**
 * Tries to autoFill the timepart of the current input if the user hasnt filled it out completely
 *
 * This requires that atleast the hour part of the time is filled out.
 *
 * Any unfilled values will be set to 0
 *
 */
const autoFillOnBlur = () => {
    // Only if the setting is enabled
    if (!props.autoFillOnBlur) {
        return;
    }

    // If we are already fully filled this is not applicable
    if (imd.isMaskFilled()) {
        return;
    }

    // We only autofill formats with time
    if (!dateFormatHasTime(props.format)) {
        return;
    }

    // Time must have atleast the hour filled.
    let minimumUnfilledCharacters = 2;

    // If there is a date in the format, the date has to be filled as well
    if (dateFormatHasDate(props.format)) {
        minimumUnfilledCharacters = 14;
    }

    if (imd.getFirstUnfilledIndex() < minimumUnfilledCharacters) {
        return;
    }

    // Emit the current displaytext directly. Whatever parts are filled will be used as is, the rest is 0'ed.
    emitStringAsDate(imd.getCurrentDisplayText());
};

defineExpose<IEditorComponent>({
    focus: () => inputMaskInstance.value?.focus(),
    validateMessage: ValidateMessage,
    KeyboardEvent: (ev: KeyboardEvent) => {
        if (inputMaskInstance.value && typeof inputMaskInstance.value.KeyboardEvent === 'function') {
            inputMaskInstance.value?.KeyboardEvent(ev);
        }
    },
});
</script>

<template>
    <!-- Add flex-fill to the btn-group to fill the entire line -->
    <ValidatorMessage
        :message="ValidateMessage"
        :hide="!hasHadFocus"
        :fill="false"
    >
        <div class="form-control-calendar flex-nowrap">
            <div
                class="btn-group"
                role="group"
            >
                <ButtonComp
                    button-type="Custom"
                    icon="fa-calendar"
                    class="btn-outline-secondary open-calendar"
                    @click="toggleCalender(!calendarOpen)"
                    ref="calendarButton"
                    v-if="!hideSelector && typesThatTriggerCalendar.includes(format)"
                />
                <ButtonComp
                    button-type="Custom"
                    icon="fa-clock"
                    class="btn-outline-secondary"
                    ref="calendarButton"
                    v-if="
                        !hideSelector &&
                        !typesThatTriggerCalendar.includes(format) &&
                        !['DATETIME', 'DATETIMEMS1', 'DATETIMEMS2', 'DATETIMEMS3'].includes(format)
                    "
                />
                <NullSetter
                    :nullable="showNullSetter"
                    v-model="maskValue"
                >
                    <InputMask
                        v-bind:model-value="maskValue"
                        :disabled="disabled"
                        class="text-end font-monospace text-lowercase"
                        :class="[
                            cssClassForDateType.has(format) ? cssClassForDateType.get(format) : 'calendar-datetime',
                            ValidateMessage != null ? 'border-0' : '',
                            $attrs.class,
                        ]"
                        ref="inputMaskInstance"
                        @update:model-value="emitStringAsDate"
                        @mask-value-changed="maskValueChanged"
                        @blur="onBlur"
                        :mask-data="imd as InputMaskData"
                        :focusKeepsCursorPosition="true"
                    />
                </NullSetter>
            </div>

            <PopupRelativeTo
                :parent-element="inputMaskInstance as ElementOrComponent"
                position="BottomRight"
            >
                <CollapseContent :visible="calendarOpen">
                    <InputDateTimeCalendar
                        v-bind:month-to-display="internalDate"
                        v-on:date-selected="calendarDateSelected"
                        ref="inputDateTimeInstance"
                    />
                </CollapseContent>
            </PopupRelativeTo>
        </div>
    </ValidatorMessage>
</template>
